import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, debounceTime, tap } from 'rxjs';
import { MenuInterface } from './api.bokkz.services';
import { ErrorMessage } from './rekenclient.api';
import { Dividendbelasting, DividendTaxEntity } from './api.bokkz.services';

@Injectable({ providedIn: 'root' })
export class BokkzDivDataService implements OnDestroy {

    public dividendTaxEntity = new BehaviorSubject<DividendTaxEntity>(this.getFromSessionStorage<DividendTaxEntity>('dividendTaxEntity') || {} as DividendTaxEntity);
    public menuInterface = new BehaviorSubject<MenuInterface[]>([]);
    public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
    public aantalValidaties = new BehaviorSubject<number>(0);
    public hasForeignShareholders = new BehaviorSubject<boolean>(false);
    public hasAfdrachtvermindering = new BehaviorSubject<boolean>(false);

    constructor() {
        this.applyDebounceTime();
    }

    private applyDebounceTime() {
        const debounceDelay = 50;
        this.dividendTaxEntity.pipe(
            debounceTime(debounceDelay),
            tap((value) => this.saveToSessionStorage('dividendTaxEntity', value))).subscribe();
        this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
        this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
    }

    private getFromSessionStorage<T>(key: string): T | null {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    }

    private saveToSessionStorage(key: string, value: any) {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    updateAangifte(divAangfite: Dividendbelasting): Dividendbelasting {
        const dividendSpecs = divAangfite.aangifteDb?.dividendSpecificaties?.at(0);
        this.hasAfdrachtvermindering.next(false);
        this.hasForeignShareholders.next(false);
        if (dividendSpecs?.afdrachtsVermindering) {
            this.hasAfdrachtvermindering.next(true);
        }
        if (dividendSpecs?.belastVerdragsTarief || divAangfite.opgaafDb) {
            this.hasForeignShareholders.next(true);
        }
        this.updateMenuInterface();
        return divAangfite;
    }

    updateDividendTaxEntity(dividendTaxEntity: DividendTaxEntity) {
        if (dividendTaxEntity?.aangifte?.contactPersoon) {
            const cp = dividendTaxEntity.aangifte.contactPersoon;
            if (cp.tussenvoegsel === '' || cp.tussenvoegsel === ' ') {
                cp.tussenvoegsel = undefined;
            }
        }

        let updatedAangifte = {} as Dividendbelasting;

        if (dividendTaxEntity.aangifte) {
            updatedAangifte = this.updateAangifte(dividendTaxEntity.aangifte);
            dividendTaxEntity.aangifte = updatedAangifte;
            this.dividendTaxEntity.next(dividendTaxEntity);
        } else {
            this.dividendTaxEntity.next(dividendTaxEntity);
        }
    }

    updateErrorResponse(errorResponse: ErrorMessage[]) {
        this.errorResponse.next(errorResponse);
    }

    updateAantalValidaties(aantalValidaties: number) {
        this.aantalValidaties.next(aantalValidaties);
    }

    updateMenuInterface() {
        const menuInterface: MenuInterface[] = [
            {
                value: 0,
                id: 'belastingplichtige',
                label: 'Algemeen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Inhoudingsplichtige',
                        pageMapping: 'Inhoudingsplichtige',
                        visible: true,
                        value: 0
                    },
                    {
                        label: 'Contactgegevens',
                        pageMapping: 'Contactgegevens',
                        visible: true,
                        value: 1
                    },
                    {
                        label: 'Vragen vooraf',
                        pageMapping: 'Vragen vooraf',
                        visible: true,
                        value: 2
                    }
                ]
            },
            {
                value: 1,
                id: 'Gerechtigden',
                label: 'Opbrengstgerechtigden',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Inhouding dividendbelasting',
                        pageMapping: 'Inhouding dividendbelasting',
                        visible: true,
                        value: 0
                    },
                    {
                        label: 'Inhouding dividendbelasting - buitenland',
                        pageMapping: 'Inhouding dividendbelasting buitenland',
                        visible: this.hasForeignShareholders.value,
                        value: 1
                    },
                    {
                        label: 'Inhoudingsvrijstelling',
                        pageMapping: 'Inhoudingsvrijstelling',
                        visible: true,
                        value: 2
                    },
                    {
                        label: 'Inhoudingsvrijstelling - buitenland',
                        pageMapping: 'Inhoudingsvrijstelling buitenland',
                        visible: this.hasForeignShareholders.value,
                        value: 3
                    }
                ]
            },
            {
                value: 5,
                id: 'Bijzonderheden',
                label: 'Bijzonderheden',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Afdrachtsvermindering',
                        pageMapping: 'Afdrachtsvermindering',
                        visible: this.hasAfdrachtvermindering.value,
                        value: 0
                    },
                    {
                        label: 'Overige vragen',
                        pageMapping: 'Overige vragen',
                        visible: true,
                        value: 1
                    }
                ]
            },
            {
                value: 6,
                id: 'overzicht',
                label: 'Overzicht',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Dividend en -belasting',
                        pageMapping: 'Dividend en -belasting',
                        visible: true,
                        value: 0
                    }
                ]
            }
        ];
        this.menuInterface.next(menuInterface);
    }

    ngOnDestroy(): void {
        if (this.menuInterface) {
            this.menuInterface?.unsubscribe();
        }
        if (this.errorResponse) {
            this.errorResponse?.unsubscribe();
        }
        if (this.aantalValidaties) {
            this.aantalValidaties?.unsubscribe();
        }
        if (this.dividendTaxEntity) {
            this.dividendTaxEntity.unsubscribe();
        }
    }
}
